/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1695686400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Sep 26 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "apollo",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Apollo.io",
          "depth": 0,
          "id": "connect-to-apolloio"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing accounts",
          "depth": 1,
          "id": "syncing-accounts"
        }, {
          "value": "Syncing contacts",
          "depth": 1,
          "id": "syncing-contacts"
        }, {
          "value": "Syncing sequences",
          "depth": 1,
          "id": "syncing-sequences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      h3: "h3",
      h4: "h4",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Accounts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to an account"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://apolloio.github.io/apollo-api-docs/?shell#accounts-api",
                children: "Account docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contacts"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a contact"
            }), _jsx(_components.td, {
              children: "Upsert, Update, Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://apolloio.github.io/apollo-api-docs/?shell#contacts-api",
                children: "Contact docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sequences"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to update membership in Apollo sequences"
            }), _jsx(_components.td, {
              children: "Insert, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://apolloio.github.io/apollo-api-docs/?shell#sequences-api",
                children: "Sequence docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-apolloio",
        children: "Connect to Apollo.io"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Apollo.io"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nYou can then authenticate Hightouch to Apollo.io with by entering an Apollo.io ", _jsx(_components.strong, {
          children: "API Key"
        }), ".\nFollow ", _jsx(_components.a, {
          href: "https://knowledge.apollo.io/hc/en-us/articles/4415734629773-Configure-Access-to-the-Apollo-REST-API",
          children: "these instructions"
        }), " to create one."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Because Apollo.io API key scopes are based on endpoints and not permissions, you must create a master key to use the Hightouch integration."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Apollo.io destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Apollo.io destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-accounts",
        children: "Syncing accounts"
      }), "\n", _jsx(_components.p, {
        children: "Account objects represent companies in Apollo.io and can be used as leads or opportunities."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match source rows to contacts in Apollo.io, you need to select a source column and corresponding Apollo.io field. You can match on any of the following Apollo.io fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Name"
        }), "\n", _jsx(_components.li, {
          children: "ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Apollo.io default and custom fields. For custom picklist fields, you can send display values, and Hightouch remaps the value to Apollo.io's internal ID."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-contacts",
        children: "Syncing contacts"
      }), "\n", _jsx(_components.p, {
        children: "Contact objects are people in Apollo.io and are used as the point-of-contact for leads and opportunities."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match source rows to contacts in Apollo.io, you need to select a source column and corresponding Apollo.io field. You can match on any of the following Apollo.io fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email (recommended)"
        }), "\n", _jsx(_components.li, {
          children: "ID"
        }), "\n", _jsx(_components.li, {
          children: "Name"
        }), "\n", _jsx(_components.li, {
          children: "Title"
        }), "\n", _jsx(_components.li, {
          children: "Company"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Apollo.io default and custom fields. For custom picklist fields, you can send display values, and Hightouch remaps the value to Apollo.io's internal ID."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-sequences",
        children: "Syncing sequences"
      }), "\n", _jsx(_components.p, {
        children: "Sequences are outreach campaigns with any number of sequential contact points and tasks that you can customize to personally engage your target audiences at scale."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to records in Apollo.io, you need to select the model column that contains values that match the ", _jsx(_components.strong, {
          children: "ID"
        }), " Apollo.io field."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
